import React, { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useMiniChat } from "../../context/MiniChatContext";
import {
  getDockReservedRight,
  subscribeDockReservedRight,
} from "../../utils/workspaceDock";
import MiniChat from "./MiniChat";

const SIDEBAR_WIDTH = 220; // matches --chat-sidebar-width
const CHAT_WIDTH = 320;
const GAP = 8;
const BASE_RIGHT = 16; // matches ChatSidebar right: 16px

// Open chatboxes follow the user across EVERY clan surface (workspace,
// administrator, commerce, reports, ...) and survive new tabs via the
// persisted MiniChat state — but they must not appear on the personal
// /account page, and the /app platform console is not a clan surface.
// The open-chat STATE is kept either way; leaving these routes brings the
// boxes straight back.
function isChatSuppressedPath(pathname) {
  const path = `${pathname || ""}`;
  return (
    path === "/account" ||
    path.startsWith("/account/") ||
    path === "/app" ||
    path.startsWith("/app/")
  );
}

/**
 * T-050 (F4) — where the first chat window starts, measured.
 *
 * The chats used to dock at a fixed offset that cleared only the chat sidebar,
 * which on the clan workspace put an open chat straight on top of the floating
 * To-do widget and whatever deck was beneath it. A tester's row `Delete` button
 * resolved to a chat bubble under `elementFromPoint` (T-044). The workspace
 * publishes how far in from the right edge its widget reaches
 * (`utils/workspaceDock.js` — 326px wide open, 248px collapsed, moved in at
 * 1180px, not floating at all below 640px), and the chats start to the LEFT of
 * that, with a gap. Off the workspace the reservation is 0 and this is exactly
 * the old offset.
 */
export function chatDockRight({ index = 0, dockReservedRight = 0 } = {}) {
  const sidebarEdge = BASE_RIGHT + SIDEBAR_WIDTH;
  const reserved = Math.max(0, Number(dockReservedRight) || 0);
  const base = Math.max(sidebarEdge, reserved);
  return base + GAP + Math.max(0, Number(index) || 0) * (CHAT_WIDTH + GAP);
}

/**
 * Renders a floating MiniChat window for each open chat,
 * positioned from right to left alongside the Chat sidebar.
 */
export default function ChatPopupManager() {
  const { openChats } = useMiniChat();
  const location = useLocation();
  const dockReservedRight = useSyncExternalStore(
    subscribeDockReservedRight,
    getDockReservedRight,
    () => 0
  );

  if (isChatSuppressedPath(location.pathname)) return null;
  if (openChats.length === 0) return null;

  const windows = (
    <>
      {openChats.map((chat, index) => (
        <MiniChat
          key={chat.id}
          chat={chat}
          rightPosition={chatDockRight({ index, dockReservedRight })}
        />
      ))}
    </>
  );

  // T-050 (F4) — a chat window is `position: fixed`, and a fixed element inside
  // a transformed ancestor is laid out against that ancestor rather than the
  // screen. Nothing between this component and the document root is transformed
  // today, but these windows outlive every page they float over: portalling to
  // the body makes that independent of whatever a future layout wraps the app
  // in. React portals keep context and event bubbling on the React tree, so
  // MiniChatContext and every handler are unaffected.
  if (typeof document === "undefined") return windows;
  return createPortal(windows, document.body);
}
